/**
 * Component: Slider Page - Carousel
 * ------------------------------------------------------------------------------
 * An interface to create carousel objects using the Swiper plugin.
 * - https://github.com/nolimits4web/swiper
 *
 * @namespace SliderPageCarousel
 */
import Swiper from 'swiper/js/swiper.min';
import breakpoints from '~/helpers/breakpoints';
import cssClasses from '../helpers/cssClasses';
import {debounce, scrollStop, on} from '../helpers/utils';

/**
 * DOM selectors.
 */
const selectors = {
  container: '[js-slider-page="container"]',
  designer: '[js-slider-page="designer"]',
  main: '[js-slider-page="main"]',
  nextSlide: '[js-slider-page="next"]',
  pagination: '[js-slider-page="pagination"]',
  paginationBul: '.swiper-pagination-bullet',
  slide: '[js-slider-page="slide"]',
  slideName: 'js-slide-name',
};

let swiper = {};
const slideNames = [];

export default () => {

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    container: document.querySelector(selectors.container),
    main: document.querySelector(selectors.main),
    nextSlide: document.querySelector(selectors.nextSlide),
    pagination: document.querySelector(selectors.pagination),
    slides: [...document.querySelectorAll(selectors.slide)],
  };

  /**
   * Get Slide names.
   */
  function getSlideNames() {
    nodeSelectors.slides.map((slide) => {
      const name = slide.getAttribute(selectors.slideName);
      return slideNames.push(name);
    });
  }

  /**
   * Construct carousel.
   */
  function constructCarousel() {
    swiper = new Swiper(nodeSelectors.container, {
      a11y: {
        enabled: true,
      },
      direction: 'vertical',
      keyboard: {
        enabled: true,
      },
      mousewheel: {
        forceToAxis: true,
        invert: true,
      },
      on: {
        slideChangeTransitionStart: () => {
          const activeSlide = document.querySelector('.swiper-slide-active');
          const buttons = [...activeSlide.querySelectorAll('button')];

          buttons.forEach((button) => {
            button.tabIndex = 0;
          });
        },
        slideChange: () => {
          const activeSlide = document.querySelector('.swiper-slide-active');
          const buttons = [...activeSlide.querySelectorAll('button')];
          window.scrollTo(0, 0);

          buttons.forEach((button) => {
            button.tabIndex = -1;
          });

          setTimeout(() => {
            swiper.params.mousewheel.releaseOnEdges = false;
            swiper.params.touchReleaseOnEdges = false;
          }, 500);
        },
        reachEnd: () => {
          setTimeout(() => {
            swiper.params.mousewheel.releaseOnEdges = true;
            swiper.params.touchReleaseOnEdges = true;
          }, 750);
        },
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
        renderBullet: (index, className) => {
          if (slideNames[index] === undefined || slideNames[index] === '') {
            return `<span class="${className}"></span>`;
          }
          return `<span class="${className}">${slideNames[index]}</span>`;
        },
      },
      simulateTouch: false,
      slidesPerView: 1,
      speed: 1000,
      observer: true,
      observeParents: true,
    });
  }

  /**
   * Initialise carousel.
   */
  function initCarousel() {
    if (window.matchMedia(`(min-width: ${breakpoints.large})`).matches) {
      if (swiper.initialized) { swiper.destroy(); }
      constructCarousel();
      swiper.init();
      return;
    }

    navMobileInit();

    if (!swiper.initialized) { return; }
    swiper.destroy();
  }

  /**
   * Mobile Navigation - sticky bar.
   */
  function navMobileSticky() {
    const element = nodeSelectors.main;

    if ((window.innerHeight + window.pageYOffset - 24) > element.clientHeight) {
      nodeSelectors.pagination.classList.remove(cssClasses.sticky);
    } else {
      nodeSelectors.pagination.classList.add(cssClasses.sticky);
    }
  }

  /**
   * Mobile navigation - populate contents.
   */
  function navMobilePopulate() {
    const buildPagination = slideNames.map((name) => {
      return `<button class="swiper-pagination-bullet" role="button" data-slide="${name}">${name}</button>`;
    }).join('');

    nodeSelectors.pagination.innerHTML = buildPagination;
  }

  /**
   * Mobile navigation - active slide.
   */
  function navMobileActiveSlide() {
    nodeSelectors.slides.forEach((slide) => {
      const name = slide.getAttribute(selectors.slideName);
      const bullet = document.querySelector(`${selectors.paginationBul}[data-slide="${name}"]`);

      if (isElementInViewport(slide)) {
        bullet.classList.add('swiper-pagination-bullet-active');
      } else {
        bullet.classList.remove('swiper-pagination-bullet-active');
      }
    });
  }

  /**
   * Checks if an element is in the viewport.
   * @param {HTMLElement} slide - Slide to check visibility of.
   */
  function isElementInViewport(slide) {
    const rect = slide.getBoundingClientRect();
    return (
      rect.bottom >= ((window.innerHeight || document.documentElement.clientHeight) / 2) &&
      rect.top <= ((window.innerHeight || document.documentElement.clientHeight) / 2)
    );
  }

  /**
   * Returns the elements offset.
   * @param {HTMLElement} element - The element.
   */
  function getElementOffset(element) {
    const offset = element.getBoundingClientRect().left;
    return offset;
  }

  function scrollPagination() {
    const bullet = document.querySelector(`${selectors.paginationBul}.swiper-pagination-bullet-active`);

    nodeSelectors.pagination.scroll({
      left: getElementOffset(bullet),
      behavior: 'smooth',
    });
  }


  /**
   *  Mobile navigation - event Listeners.
   */
  function navMobileEventListeners() {
    on('scroll', debounce(navMobileSticky), 150);
    on('scroll', debounce(navMobileActiveSlide), 150);
    scrollStop(scrollPagination);

    on('click', nodeSelectors.pagination, (event) => {
      const target = event.target;

      if (!(target && target.matches('button'))) { return; }
      const title = target.innerText;
      document.querySelector(`[js-slide-name="${title}"]`).scrollIntoView();
    });
  }

  /**
   *  Mobile navigation - Init function.
   */
  function navMobileInit() {
    navMobilePopulate();
    navMobileSticky();
    navMobileEventListeners();
  }

  /**
   * Handle scrolling events using scroll top position.
   */
  function handleScrollEvent() {
    const scrollPosition = window.pageYOffset;

    if (scrollPosition === 0) {
      nodeSelectors.container.classList.remove(cssClasses.disabled);
    } else {
      nodeSelectors.container.classList.add(cssClasses.disabled);
    }
  }

  /**
   * Check if pagination is empty.
   */
  function paginationCheck() {
    let isEmpty = true;

    [...document.querySelectorAll(selectors.paginationBul)].forEach((item) => {
      if (item.innerHTML === '') { return; }
      isEmpty = false;
    });

    if (!isEmpty) { return; }
    nodeSelectors.main.classList.add('no-pagination');
  }

  /**
   * Set event listeners.
   */
  function setEventListeners() {
    on('resize', debounce(() => initCarousel(), 350));
    on('scroll', debounce(() => handleScrollEvent(), 150));
    on('click', nodeSelectors.nextSlide, () => { swiper.slideNext(); });

    on('click', nodeSelectors.pagination, (event) => {
      const target = event.target;

      if (!(target && target.matches('span'))) { return; }
      window.scrollTo(0, 0);
    });
  }

  /**
   * Initialise components.
   */
  function init() {
    window.scrollTo(0, 0);
    getSlideNames();
    initCarousel();
    setEventListeners();
    paginationCheck();

    setTimeout(() => {
      nodeSelectors.main.classList.add(cssClasses.active);
    }, 1000);
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  });
};
